import React from "react";
import axios from "axios";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MicButton — records mic audio, uploads to /api/stt/transcribe (Emergent Whisper),
 * returns the transcript via onTranscript(text).
 * Auto-stops after `maxSeconds` (default 20s) so users can't over-record.
 */
export default function MicButton({ onTranscript, language, maxSeconds = 20, className = "", testId = "mic-btn" }) {
  const [state, setState] = React.useState("idle"); // idle | recording | uploading
  const recorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const stopTimeoutRef = React.useRef(null);
  const streamRef = React.useRef(null);

  React.useEffect(() => () => cleanup(), []);

  const cleanup = () => {
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        cleanup();
        if (blob.size < 500) { setState("idle"); toast.error("Recording too short"); return; }
        setState("uploading");
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const url = `${API}/stt/transcribe${language ? `?language=${language}` : ""}`;
          const res = await axios.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
          const text = (res.data?.text || "").trim();
          if (!text) toast.warning("Couldn't hear you clearly");
          onTranscript?.(text);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Transcription failed");
        } finally { setState("idle"); }
      };
      rec.start();
      setState("recording");
      stopTimeoutRef.current = setTimeout(() => rec.state === "recording" && rec.stop(), maxSeconds * 1000);
    } catch (e) {
      toast.error("Microphone permission denied");
      setState("idle");
    }
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state === "recording") recorderRef.current.stop();
  };

  const onClick = () => {
    if (state === "idle") start();
    else if (state === "recording") stop();
  };

  const Icon = state === "uploading" ? Loader2 : state === "recording" ? Square : Mic;
  const label = state === "uploading" ? "Transcribing…" : state === "recording" ? "Stop" : "Voice search";

  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={state === "uploading"}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border transition-all ${
        state === "recording"
          ? "bg-rose-500 border-rose-500 text-white animate-pulse"
          : state === "uploading"
          ? "bg-[color:var(--forest-soft)] border-[color:var(--forest)] text-[color:var(--forest)]"
          : "bg-white border-[color:var(--line-strong)] text-[color:var(--ink-2)] hover:border-[color:var(--forest)] hover:text-[color:var(--forest)]"
      } ${className}`}
      aria-label={label}
      title={label}
    >
      <Icon className={`w-4 h-4 ${state === "uploading" ? "animate-spin" : ""}`} />
      <span className="text-xs font-semibold">{label}</span>
    </button>
  );
}
